// PocketModeOverlay  (UI-Name: "Battery Safe Mode")
// ==================================================
//
// Wave 14ca-fix64: OLED-Schwarz-Lock-Overlay fuer Hosentaschen-Recording.
// Wave 14ds: aus dem Voll-Schwarz wird "Battery Safe Mode" mit einer kleinen
// Mini-Map-"Briefmarke" oben-mittig — der Rest bleibt OLED-schwarz.
//
// Konzept: vollflaechiger schwarzer Layer, OLED-Pixel sind physikalisch
// AUS → ~50-60 % weniger Display-Verbrauch. Recording laeuft ungestoert
// weiter (Wake-Lock haelt App im Vordergrund, watchPosition feuert).
//
// Mini-Map ("Briefmarke", Wave 14ds):
//   - Portrait-Rechteck ~100×140px, oben-mittig, schwarzer BG (verschmilzt
//     mit dem OLED-Schwarz drumherum → nur die weissen Linien leuchten).
//   - Inhalt: geplante Route (routeBus) als weisse Linie, aktuelle Position
//     (positionBus) als weisser Punkt im Zentrum, Heading als kurze Linie.
//   - Canvas, NICHT zweite MapLibre-Instanz: eine zweite Map wuerde GPU +
//     Tiles laufen lassen und den OLED-Spareffekt zunichte machen. Das
//     Canvas zeichnet nur ein paar weisse Linien — vernachlaessigbarer
//     Verbrauch.
//   - Simple equirectangular-Projektion zentriert auf die Position. Bei der
//     ~200m-Briefmarke reicht das voellig (Lat-Skala = cos(centerLat) ×
//     Lon-Skala). Kein WebMercator noetig.
//   - Redraw nur bei Position-/Route-Update (~1 Hz vom GPS) — kein rAF-Loop,
//     Battery-Effizienz vor Smoothness.
//   - Kein Route im Bus (freier Ride): nur der Positions-Punkt, kein
//     Leerzustand der "verschwindet".
//
// Entsperren: 3-Finger-Long-Press 2s (gleiche Geste wie Rain-Mode).

import { useEffect, useRef, useState } from 'react';
import { pocketModeBus } from '../lib/pocketModeBus';
import { positionBus } from '../lib/positionBus';
import { routeBus, type RoutePoint } from '../lib/routeBus';
import { pathsBus } from '../lib/pathsBus';
import { rideRecorder } from '../lib/rideRecorder';
import { useBatterySafeSize, BATTERY_SAFE_PRESETS } from '../lib/useUiSettings';
import { usePreference } from '../lib/usePreference';
import {
  loadPoisWithRefresh,
  POI_CATEGORIES,
  type POI,
  type PoiCategoryId,
} from '../lib/poi';
import { kmhFromCoords, formatKmh } from '../lib/speed';

// Kategorien, die im Battery-Safe-Mode-Menü angeboten werden (Reihenfolge =
// Anzeige). Default-AN: Wasser, Supermarkt, Bäckerei, Imbiss (User-Wunsch).
const BATTERY_POI_ORDER: PoiCategoryId[] = [
  'drinking_water',
  'supermarket',
  'bakery',
  'fast_food',
  'fuel_shop',
  'bicycle_shop',
  'camp_site',
  'shelter',
  'swimming',
  'restaurant_indian',
  'decathlon',
];
const BATTERY_POI_DEFAULT = 'drinking_water,supermarket,bakery,fast_food';

const UNLOCK_HOLD_MS = 2000;
const UNLOCK_TOUCH_COUNT = 3;

/** Sichtbarer Radius (Meter) von der Position bis zum oberen/unteren Rand. */
const VISIBLE_RADIUS_M = 200;
const M_PER_DEG_LAT = 111320;

export function PocketModeOverlay() {
  const [active, setActive] = useState(() => pocketModeBus.get());
  useEffect(() => {
    setActive(pocketModeBus.get());
    return pocketModeBus.subscribe(setActive);
  }, []);

  // Wave 14ds: Position + Route für die Mini-Map.
  const [pos, setPos] = useState<GeolocationCoordinates | null>(() =>
    positionBus.get(),
  );
  const [route, setRoute] = useState<RoutePoint[] | null>(() => routeBus.get());
  const [paths, setPaths] = useState<RoutePoint[][] | null>(() =>
    pathsBus.get(),
  );
  useEffect(() => {
    setPos(positionBus.get());
    return positionBus.subscribe(setPos);
  }, []);
  useEffect(() => {
    setRoute(routeBus.get());
    return routeBus.subscribe(setRoute);
  }, []);
  useEffect(() => {
    setPaths(pathsBus.get());
    return pathsBus.subscribe(setPaths);
  }, []);

  // Wave 14ds: Briefmarken-Größe aus Settings (zentrale Presets).
  const [sizePref] = useBatterySafeSize();
  const { w: STAMP_W, h: STAMP_H, top: STAMP_TOP } =
    BATTERY_SAFE_PRESETS[sizePref];

  // Wave 14eo / ULTRA: POI-Menü im Battery-Safe-Mode. Mehrere Kategorien
  // togglebar (User: "mehr Point of Interest als nur Wasseranzeigen, am besten
  // ein kleines Menü"). Persistiert als Komma-Liste in localStorage. Default:
  // Wasser, Supermarkt, Bäckerei, Imbiss.
  const [enabledStr, setEnabledStr] = usePreference(
    'adt_pocket_pois',
    BATTERY_POI_DEFAULT,
  );
  const enabled = new Set(
    enabledStr.split(',').filter(Boolean) as PoiCategoryId[],
  );
  const toggleCat = (cat: PoiCategoryId) => {
    const next = new Set(enabled);
    if (next.has(cat)) next.delete(cat);
    else next.add(cat);
    setEnabledStr(BATTERY_POI_ORDER.filter((c) => next.has(c)).join(','));
  };
  const [menuOpen, setMenuOpen] = useState(false);

  // POIs je aktivierter Kategorie (aus IDB-Cache, kein Network). Einmal pro
  // Kategorie geladen und im State gehalten.
  const [poiData, setPoiData] = useState<Partial<Record<PoiCategoryId, POI[]>>>(
    {},
  );
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    for (const cat of enabled) {
      if (poiData[cat] !== undefined) continue; // schon geladen
      loadPoisWithRefresh(cat)
        .then((res) => {
          if (!cancelled) setPoiData((prev) => ({ ...prev, [cat]: res.pois }));
        })
        .catch(() => {
          if (!cancelled) setPoiData((prev) => ({ ...prev, [cat]: [] }));
        });
    }
    return () => {
      cancelled = true;
    };
  }, [active, enabledStr, poiData]);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Wave 14ds: Canvas-Redraw bei Position-/Route-Änderung. Da positionBus
  // ~1 Hz vom GPS getrieben wird, ist das natürlich gedrosselt — kein
  // rAF-Loop, kein Dauerverbrauch.
  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    // Wave 14ds-fix: BEIDE Dimensionen prüfen. Vorher nur width — bei dpr=3
    // (iPhone Pro) ist STAMP_W*dpr = 300 = Default-Canvas-Breite, der Check
    // schlug fehl, height blieb beim Default 150 statt 420 → vertikales
    // Clipping, Briefmarke wirkte leer.
    if (canvas.width !== STAMP_W * dpr || canvas.height !== STAMP_H * dpr) {
      canvas.width = STAMP_W * dpr;
      canvas.height = STAMP_H * dpr;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, STAMP_W, STAMP_H);

    // Wave 14ds-fix2: Rahmen IM Canvas (statt CSS-Border) — 0.5px-Offset für
    // eine crispe 1px-Linie. Garantiert exaktes Center-Mapping.
    ctx.strokeStyle = 'rgba(255,255,255,0.18)';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, STAMP_W - 1, STAMP_H - 1);

    const cx = STAMP_W / 2;
    const cy = STAMP_H / 2;

    if (!pos) {
      // Wave 14ds-fix: Kein GPS-Fix → hohl-Kreis + "no GPS"-Label als
      // sichtbares Lebenszeichen. Vorher nur ein 4px-Punkt (zu subtil → User
      // las die Briefmarke als leer/kaputt). Hohl-Kreis (3px Stroke) ist
      // deutlich sichtbar, das Label erklärt warum sonst nichts da ist.
      ctx.strokeStyle = 'rgba(255,255,255,0.85)';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(cx, cy - 6, 7, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      ctx.font = '10px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('no GPS', cx, cy + 12);
      return;
    }

    const centerLat = pos.latitude;
    const centerLon = pos.longitude;
    const mPerDegLon = M_PER_DEG_LAT * Math.cos((centerLat * Math.PI) / 180);
    const pxPerM = STAMP_H / 2 / VISIBLE_RADIUS_M;

    const project = (lat: number, lon: number): [number, number] => {
      const dxm = (lon - centerLon) * mPerDegLon;
      const dym = (lat - centerLat) * M_PER_DEG_LAT;
      return [cx + dxm * pxPerM, cy - dym * pxPerM]; // North-up: +lat → -y
    };

    // Wave 14ds-fix3: Heading-Up. Welt-Layer (nearby/breadcrumb/route) werden
    // um -heading um den Center rotiert, sodass die Fahrtrichtung IMMER nach
    // oben zeigt. Position-Punkt + Skala + Rahmen bleiben im fixen Frame
    // (nach restore gezeichnet). Bei heading==null (Stillstand) keine
    // Rotation — bleibt north-up.
    const heading = pos.heading;
    const headingUp = heading != null && !Number.isNaN(heading);
    ctx.save();
    if (headingUp) {
      ctx.translate(cx, cy);
      ctx.rotate((-heading! * Math.PI) / 180);
      ctx.translate(-cx, -cy);
    }

    // Nearby paths (Wave 14ds-fix3): umliegende Trails als dezenter Kontext,
    // GANZ unten in der Z-Order. Sehr dünn + dimm. bbox-Filter: nur Pfade
    // zeichnen die mindestens einen Punkt im sichtbaren Radius (+50% Marge)
    // haben — der Rest wird übersprungen (Perf bei vielen Trails).
    if (paths && paths.length > 0) {
      const marginDeg = (VISIBLE_RADIUS_M * 1.5) / M_PER_DEG_LAT;
      const lonMarginDeg = marginDeg / Math.max(0.1, Math.cos((centerLat * Math.PI) / 180));
      ctx.strokeStyle = 'rgba(255,255,255,0.2)';
      ctx.lineWidth = 0.75;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      for (const path of paths) {
        if (path.length < 2) continue;
        let near = false;
        for (const [lat, lon] of path) {
          if (
            Math.abs(lat - centerLat) <= marginDeg &&
            Math.abs(lon - centerLon) <= lonMarginDeg
          ) {
            near = true;
            break;
          }
        }
        if (!near) continue;
        ctx.beginPath();
        path.forEach(([lat, lon], i) => {
          const [x, y] = project(lat, lon);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.stroke();
      }
    }

    // Breadcrumb-Layer (aufgezeichneter Track) — über nearby, unter Route.
    const trackPoints = rideRecorder.getPoints();
    if (trackPoints.length > 1) {
      ctx.strokeStyle = 'rgba(255,255,255,0.5)';
      ctx.lineWidth = 1.5;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.beginPath();
      trackPoints.forEach((tp, i) => {
        const [x, y] = project(tp.lat, tp.lon);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
    }

    // Geplante Route — dickste/prominenteste Linie ("wo muss ich hin").
    // Wave 14ds-fix3: 3px (vorher 2px) auf User-Wunsch.
    if (route && route.length > 1) {
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.beginPath();
      route.forEach(([lat, lon], i) => {
        const [x, y] = project(lat, lon);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
    }

    ctx.restore(); // zurück in den fixen Frame (keine Rotation mehr)

    // Wave 14eo / 14ev: Wasserstellen als aufrechte Tropfen-Glyphen. NACH
    // dem restore im fixen Frame gezeichnet, damit die Tropfen NICHT mit dem
    // Heading-Up mitrotieren (sonst kopfüber bei Südfahrt). Die Screen-
    // Position wird manuell um den Center rotiert (gleiche Transform wie der
    // Welt-Layer), der Glyph selbst bleibt aufrecht. Größer (~12×19px) statt
    // dem alten 2px-Punkt — beim Vorbeifahren mit einem Blick erkennbar.
    // Z-Order: über Trails/Route (nach restore), unter dem Position-Punkt.
    {
      const marginDeg = (VISIBLE_RADIUS_M * 1.1) / M_PER_DEG_LAT;
      const lonMarginDeg =
        marginDeg / Math.max(0.1, Math.cos((centerLat * Math.PI) / 180));
      const a = headingUp ? (-heading! * Math.PI) / 180 : 0;
      const cosA = Math.cos(a);
      const sinA = Math.sin(a);
      // Wasser-Tropfen (Spitze oben, runder Bauch) — wie bisher, gut erkennbar.
      const drawDroplet = (px: number, py: number) => {
        const r = 6;
        const tipY = py - 11;
        const bulbCY = py + 2;
        ctx.beginPath();
        ctx.moveTo(px, tipY);
        ctx.quadraticCurveTo(px + r, bulbCY - r, px + r, bulbCY);
        ctx.arc(px, bulbCY, r, 0, Math.PI, false);
        ctx.quadraticCurveTo(px - r, bulbCY - r, px, tipY);
        ctx.closePath();
        ctx.fillStyle = 'rgba(255,255,255,0.9)';
        ctx.fill();
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'rgba(0,0,0,0.5)';
        ctx.stroke();
      };
      // Andere Kategorien als farbiger Punkt mit dunklem Rand — beim
      // Vorbeiradeln per Farbe unterscheidbar.
      const drawDot = (px: number, py: number, color: string) => {
        ctx.beginPath();
        ctx.arc(px, py, 5, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.lineWidth = 1.2;
        ctx.strokeStyle = 'rgba(0,0,0,0.6)';
        ctx.stroke();
      };
      for (const cat of BATTERY_POI_ORDER) {
        if (!enabled.has(cat)) continue;
        const list = poiData[cat];
        if (!list || list.length === 0) continue;
        const color = POI_CATEGORIES[cat].color ?? 'rgba(255,255,255,0.9)';
        for (const p of list) {
          if (
            Math.abs(p.lat - centerLat) > marginDeg ||
            Math.abs(p.lon - centerLon) > lonMarginDeg
          ) {
            continue;
          }
          const [px0, py0] = project(p.lat, p.lon);
          // Welt-Rotation manuell auf den projizierten Punkt anwenden, damit
          // der Marker an der korrekten (mitgedrehten) Stelle sitzt — aber
          // der Glyph selbst aufrecht gezeichnet wird.
          const dx = px0 - cx;
          const dy = py0 - cy;
          const px = cx + dx * cosA - dy * sinA;
          const py = cy + dx * sinA + dy * cosA;
          if (cat === 'drinking_water') drawDroplet(px, py);
          else drawDot(px, py, color);
        }
      }
    }

    // Heading-Up-Marker: statischer "↑"-Pfeil aus dem Center nach oben —
    // signalisiert "vorne ist oben". Nur wenn ein Heading da ist; bei
    // Stillstand reicht der Positions-Punkt.
    if (headingUp) {
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      const len = 13;
      const tipY = cy - len;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx, tipY);
      ctx.moveTo(cx - 4, tipY + 5);
      ctx.lineTo(cx, tipY);
      ctx.lineTo(cx + 4, tipY + 5);
      ctx.stroke();
    }

    // Aktuelle Position als weisser Punkt im Zentrum (exakt cx/cy).
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(cx, cy, 4, 0, Math.PI * 2);
    ctx.fill();

    // Wave 14eo: User-Wunsch — fix 100 m statt auto-skaliertem 25/50 m
    // ("die skala so ist natürlich totaler quatsch, 25m und so klein,
    // das sieht man nicht beim radfahren"). 100m ist gut lesbar im
    // Vorbeiradeln und entspricht dem mentalen Modell "ein Block".
    const scaleM = 100;
    const scaleLenPx = scaleM * pxPerM;
    const barY = STAMP_H - 8;
    const barX0 = 6;
    const barX1 = barX0 + scaleLenPx;
    ctx.strokeStyle = 'rgba(255,255,255,0.7)';
    ctx.lineWidth = 1.5;
    ctx.lineCap = 'butt';
    ctx.beginPath();
    ctx.moveTo(barX0, barY);
    ctx.lineTo(barX1, barY);
    ctx.moveTo(barX0, barY - 3);
    ctx.lineTo(barX0, barY + 3);
    ctx.moveTo(barX1, barY - 3);
    ctx.lineTo(barX1, barY + 3);
    ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.font = '9px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'bottom';
    ctx.fillText(`${scaleM} m`, barX0, barY - 4);
  }, [active, pos, route, paths, STAMP_W, STAMP_H, enabledStr, poiData]);

  const [progress, setProgress] = useState(0);
  const holdStartRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!active) {
      holdStartRef.current = null;
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      setProgress(0);
      return;
    }
    const overlay = overlayRef.current;
    if (!overlay) return;

    const tick = () => {
      const start = holdStartRef.current;
      if (start == null) {
        setProgress(0);
        rafRef.current = null;
        return;
      }
      const elapsed = Date.now() - start;
      const p = Math.min(1, elapsed / UNLOCK_HOLD_MS);
      setProgress(p);
      if (p >= 1) {
        holdStartRef.current = null;
        setProgress(0);
        pocketModeBus.deactivate();
        rafRef.current = null;
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length >= UNLOCK_TOUCH_COUNT) {
        if (holdStartRef.current == null) {
          holdStartRef.current = Date.now();
          if (rafRef.current == null) {
            rafRef.current = requestAnimationFrame(tick);
          }
        }
      }
      e.preventDefault();
      e.stopPropagation();
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < UNLOCK_TOUCH_COUNT) {
        holdStartRef.current = null;
      }
      e.preventDefault();
      e.stopPropagation();
    };
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const onClick = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    overlay.addEventListener('touchstart', onTouchStart, { passive: false });
    overlay.addEventListener('touchend', onTouchEnd, { passive: false });
    overlay.addEventListener('touchcancel', onTouchEnd, { passive: false });
    overlay.addEventListener('touchmove', onTouchMove, { passive: false });
    overlay.addEventListener('click', onClick, { capture: true });
    return () => {
      overlay.removeEventListener('touchstart', onTouchStart);
      overlay.removeEventListener('touchend', onTouchEnd);
      overlay.removeEventListener('touchcancel', onTouchEnd);
      overlay.removeEventListener('touchmove', onTouchMove);
      overlay.removeEventListener('click', onClick, { capture: true });
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      holdStartRef.current = null;
    };
  }, [active]);

  if (!active) return null;

  return (
    <>
    <div
      ref={overlayRef}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        // PURE SCHWARZ — OLED-Pixel komplett aus, null Strom.
        background: '#000000',
        touchAction: 'none',
      }}
      role="alertdialog"
      aria-label="Battery safe mode active — display dark to save battery"
    >
      {/* Wave 14ds: Mini-Map-Briefmarke oben-mittig. Schwarzer Canvas-BG
          verschmilzt mit dem OLED-Schwarz; nur die weissen Linien leuchten.
          80px vom oberen Rand → sicher unter Notch/Status-Bar. */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: `calc(env(safe-area-inset-top, 0px) + ${STAMP_TOP}px)`,
          left: '50%',
          transform: 'translateX(-50%)',
          width: `${STAMP_W}px`,
          height: `${STAMP_H}px`,
          // Wave 14ds-fix2: KEINE CSS-Border mehr. Mit Tailwind's globalem
          // box-sizing:border-box hätte eine 1px-Border die Content-Box auf
          // 98×138 verkleinert während der Backing-Store 100×140 logisch ist
          // → Browser skaliert/verschiebt → Position-Punkt nicht pixelgenau
          // zentriert (User-Bug). Der Rahmen wird jetzt IM Canvas gezeichnet
          // (strokeRect), damit Content-Box = Backing-Logik = exakt 100×140.
          borderRadius: 8,
          pointerEvents: 'none',
        }}
        aria-hidden="true"
      />

      {/* Aktuelle Geschwindigkeit — großer weißer Wert unter dem Stamp.
          Reiner Text auf OLED-Schwarz → vernachlässigbarer Verbrauch. */}
      <div
        style={{
          position: 'absolute',
          top: `calc(env(safe-area-inset-top, 0px) + ${STAMP_TOP + STAMP_H + 18}px)`,
          left: 0,
          right: 0,
          textAlign: 'center',
          color: 'rgba(255,255,255,0.92)',
          pointerEvents: 'none',
        }}
        aria-hidden="true"
      >
        <span style={{ fontSize: 56, fontWeight: 800, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
          {formatKmh(kmhFromCoords(pos))}
        </span>
        <span style={{ fontSize: 18, fontWeight: 600, marginLeft: 6, color: 'rgba(255,255,255,0.6)' }}>
          km/h
        </span>
      </div>

      {/* Wave 14eo: grüne Pille entfernt (User: "braucht man unten nicht").
          3-Finger-Geste bleibt funktional als Backup, der X-Button unten
          rechts ist der primaere Exit. */}
    </div>

      {/* Exit + POI-Menü unten rechts. Beide gross (56×56), handschuh-
          freundlich. Der POI-Menü-Button (Map-Pin) öffnet ein Panel zum
          Togglen mehrerer Kategorien (Wasser, Supermarkt, Bäckerei, Imbiss …).
          Als SIBLINGS des Overlay-Divs (nicht Child) gerendert, sonst schluckt
          der Capture-Click-Handler des Overlays (3-Finger-Geste) den Tap.
          zIndex 201/202 > Overlay 200. */}
      {/* POI-Menü-Panel — öffnet über dem Menü-Button. Kategorie antippen =
          an/aus. Großzügige Tap-Targets für Handschuhe. */}
      {menuOpen && (
        <div
          style={{
            position: 'fixed',
            right: 'calc(env(safe-area-inset-right, 0px) + 1.5rem)',
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.5rem + 64px)',
            zIndex: 202,
            width: 230,
            maxHeight: '60vh',
            overflowY: 'auto',
            background: 'rgba(20,20,20,0.96)',
            border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: 14,
            padding: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            touchAction: 'manipulation',
          }}
        >
          <div
            style={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              padding: '2px 6px 4px',
            }}
          >
            POIs auf der Karte
          </div>
          {BATTERY_POI_ORDER.map((cat) => {
            const on = enabled.has(cat);
            const def = POI_CATEGORIES[cat];
            return (
              <button
                key={cat}
                type="button"
                onClick={() => toggleCat(cat)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  minHeight: 40,
                  padding: '0 8px',
                  borderRadius: 10,
                  border: on
                    ? '1px solid rgba(255,255,255,0.5)'
                    : '1px solid rgba(255,255,255,0.12)',
                  background: on ? 'rgba(255,255,255,0.12)' : 'transparent',
                  color: 'rgba(255,255,255,0.92)',
                  fontSize: 14,
                  textAlign: 'left',
                  touchAction: 'manipulation',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: def.color ?? 'rgba(255,255,255,0.9)',
                    flexShrink: 0,
                    border: '1px solid rgba(0,0,0,0.5)',
                  }}
                />
                <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {def.emoji} {def.label}
                </span>
                <span style={{ color: on ? '#34d399' : 'rgba(255,255,255,0.3)', fontSize: 16 }}>
                  {on ? '✓' : ''}
                </span>
              </button>
            );
          })}
        </div>
      )}
      <button
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        aria-label="POI menu"
        title="POIs ein-/ausblenden"
        style={{
          position: 'fixed',
          right: 'calc(env(safe-area-inset-right, 0px) + 1.5rem + 64px)',
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)',
          zIndex: 201,
          width: 56,
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          background: 'rgba(35,35,35,0.85)',
          border: menuOpen
            ? '1px solid rgba(255,255,255,0.6)'
            : '1px solid rgba(255,255,255,0.4)',
          color: 'rgba(255,255,255,0.9)',
          touchAction: 'manipulation',
        }}
      >
        <svg
          viewBox="0 0 24 24"
          width={26}
          height={26}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          aria-hidden="true"
          style={{ display: 'block' }}
        >
          {/* Map-Pin */}
          <path d="M12 21s-6-5.2-6-10a6 6 0 0 1 12 0c0 4.8-6 10-6 10z" />
          <circle cx="12" cy="11" r="2" />
        </svg>
      </button>
      <button
        type="button"
        onClick={() => pocketModeBus.deactivate()}
        aria-label="Exit battery safe mode"
        style={{
          position: 'fixed',
          right: 'calc(env(safe-area-inset-right, 0px) + 1.5rem)',
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)',
          zIndex: 201,
          width: 56,
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          // Wave 14eo: heller damit der Button im OLED-Schwarz sichtbar bleibt.
          background: 'rgba(35,35,35,0.85)',
          border: '1px solid rgba(255,255,255,0.55)',
          color: 'rgba(255,255,255,0.9)',
          touchAction: 'manipulation',
        }}
      >
        <svg
          viewBox="0 0 24 24"
          width={26}
          height={26}
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M18 6 6 18" />
          <path d="m6 6 12 12" />
        </svg>
      </button>
    </>
  );
}
